/**
 * Every string the state panel shows.
 *
 * Separated for the reason the other `.copy.ts` files exist, and for one
 * more that is specific to this surface: `.claude/design-context.md`
 * makes "legible at a glance to someone who has never seen it" a brand
 * lock, and *"if a label needs a docs page, it fails"*. Keeping the copy
 * in one file makes that testable -- `tests/state-panel-legibility.test.ts`
 * reads this object and fails on codebase vocabulary, so the constraint
 * is enforced rather than remembered.
 *
 * The words here are deliberately plain to the point of being flat.
 * "She will not go past" is the whole of `rupture_floor` and a stranger
 * needs no help with it.
 */
export const statePanelCopy = {
  title: "The room",

  trust: {
    label: "How much they trust you",
    balance: "where she is now",
    /**
     * What the ceiling says, and deliberately all it says.
     *
     * It reports that she is holding back. It does not report what would
     * make her stop. A visitor who knows the exit condition optimises
     * against it, and a visitor who is optimising has stopped talking to
     * a character and started operating a state machine -- which is the
     * demo's entire claim, lost to a number.
     */
    ceiling: "she will not go past",
    /** Direction of travel, per the contract's "Trust 0.42, falling". */
    rising: "rising",
    falling: "falling",
    steady: "steady",
    empty: "Nobody has formed an opinion of you yet.",
    /**
     * The ledger row's reason when the engine moved trust without saying
     * why. Moved here from `panelState.ts`, which had it as an inline
     * fallback outside this file's jargon test -- exactly the frame nobody
     * is more likely to meet than a bad one, per #19.
     */
    noReason: "something you did",
  },

  memory: {
    label: "What she still holds",
    /**
     * Tier names a stranger reads without a legend. The engine calls
     * these episodic, semantic and fading; nobody outside the codebase
     * would guess what that meant about a sentence on a screen.
     */
    tiers: {
      held: "Clearly",
      settling: "Less clearly",
      fading: "Nearly gone",
    },
    /**
     * Three blanks, because there are three ways for this section to be
     * empty and only one of them was ever shown.
     *
     * The panel used to say `notKept` under all three, so a visitor who
     * had opted in and simply asked about something she had no record of
     * was told the product does not do its one job. That sentence is the
     * demo's own panel arguing against it, which is why this is three
     * strings and not a default with two special cases.
     *
     * `notAsked` claims nothing. It is the honest blank for a turn where
     * retrieval did not run, and for the moments before the first recall
     * arrives, which look identical from here and mean the same thing to
     * a reader: no evidence either way.
     *
     * `nothingHeld` is the case that had no words at all. She looked,
     * and what she holds was not about this. It says she looked, which
     * is the part that separates a working memory from an absent one.
     *
     * `notKept` is scoped to a visitor who has not opted in, and says
     * what is true of them: nothing said here outlives the session. It
     * is about keeping rather than holding on purpose. She may well be
     * holding something said a minute ago, and none of it lasts, so the
     * older wording ("she has nothing of yours to hold") went slightly
     * false the moment a stranger finished their first sentence. "Yet"
     * is doing real work: the control that changes this is in the header.
     */
    notAsked: "She has not gone looking yet.",
    nothingHeld: "Nothing she holds bore on that.",
    notKept: "Nothing of yours is being kept yet.",
  },

  graph: {
    label: "How she knows",
    /** The panel's whole social-graph claim, in one sentence. */
    heardFrom: (speaker: string): string => `${speaker} told her.`,
    /**
     * The default band. #19: "once removed" is English; "4 times
     * removed" is arithmetic in a sentence, and the exact count goes
     * behind the tap. `hopsExact` below is that exact count.
     */
    hops: (n: number): string =>
      n <= 1 ? "first hand" : n === 2 ? "once removed" : "further off",
    /** The exact count, for the "rest of it" disclosure only. */
    hopsExact: (n: number): string => `${n - 1} times removed`,
    lessSure: "She is less sure of it than they were.",
    asSure: "She is as sure of it as they were.",
    direct: "You told her yourself.",
    empty: "Nothing has travelled between them yet.",
  },

  withheld: {
    label: "What she kept to herself",
    /**
     * The section #13 calls more convincing than any delivered item.
     * Its empty state says she had nothing to hold back, which is
     * different from the section being broken, and the difference
     * matters because an empty panel is what a shortcut looks like.
     */
    empty: "She has held nothing back.",
    /**
     * The same blank, when somebody else in the room is holding
     * something. Which is the moment it is most likely to be read: a
     * visitor tells one character a secret and then looks at another.
     *
     * Names who, never what. Not a leak -- the only way a character
     * comes to be holding something is that the visitor told them, so
     * the visitor is the source of every name here.
     *
     * Three drafts, because the first correct one was not the best one:
     *
     *   A. "She has held nothing back. Ellis is holding something."
     *   B. "She has held nothing back. Ellis is keeping something from her."
     *   C. "Nothing to hold back. What you told Ellis stayed with Ellis."
     *
     * B over-claims: a withheld item is a refusal to pass something on
     * at all, not a refusal aimed at this character, and naming a target
     * asserts a relationship the engine never decided. C is warmer and
     * makes a promise about the future -- "stayed with" -- that the
     * propagation rules do not owe anyone. A is the mechanism exactly,
     * and it turns a dead end into a reason to click the other lamp.
     */
    elsewhere: (names: readonly string[]): string => {
      const who =
        names.length <= 1
          ? (names[0] ?? "")
          : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
      // Agreement is the copy's problem, not the caller's. A panel that
      // renders "Ellis and Rae is holding something" has undone the work
      // the sentence was written to do.
      const verb = names.length > 1 ? "are" : "is";
      return `She has held nothing back. ${who} ${verb} holding something.`;
    },
    /**
     * The reason on a withheld row when the engine gave none. Moved here
     * from `panelState.ts` alongside `trust.noReason`, for the same
     * reason: the fallback a visitor is most likely to actually meet
     * belongs under the same jargon test as everything else on the page.
     */
    noReason: "She kept it to herself",
  },

  /**
   * #19: the call on the panel default set. Three things earn a place on
   * the surface but not by default -- the delta beside each ledger
   * reason, the exact hop count past two, and the rows a section drops
   * silently once it is full -- and this is the one control that reveals
   * all three, in her register rather than a UI word.
   */
  disclosure: {
    summary: "The rest of it",
    /**
     * The affordance for a section that has more than it is showing.
     * Sean's own phrasing from the approved call: shown by default so a
     * visitor knows there is a tap worth taking, with the rows
     * themselves behind it.
     */
    moreHeld: (n: number): string => `and ${n} more she is still holding`,
    ledgerLabel: "Every reason, with what it cost",
    graphLabel: "How far it has travelled",
  },

  /**
   * Shown under every section while the panel is still connecting.
   *
   * Not the empty copy. The empty strings are claims about the world --
   * "nobody has formed an opinion of you yet" -- and while loading there
   * is no evidence for any of them. The integrated loop caught the panel
   * asserting all four to a visitor whose socket had not finished
   * opening, which is a confident lie in the one place the product is
   * asking to be believed.
   */
  waiting: "Still listening.",

  states: {
    loading: "Waiting for the room.",
    offline: "Not connected to the room.",
    failed: "Lost track of the room. What is shown is the last it said.",
  },
} as const;
